import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo
} from "react";
import {
	Calendar,
	CalendarHighlightDateOnlyRange,
	CalendarHighlightDateRange,
	CalendarViewKind,
	useCalendar
} from "client/Components";
import { useValueEqualMemo } from "client/Hooks";
import { isNil } from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { convertToDateOnly, convertToDateTime } from "client/DateAndTime/dateUtils";

export type CalendarDateSelectorProps = {
	date: DateOnly | undefined;
	calculateHighlightRangeForDate?(date: DateOnly): CalendarHighlightDateOnlyRange;
	onChange(date: DateOnly): void;
	timezone: TimeZone;
	trackingId: string;
};

export const CalendarDateSelector = (props: CalendarDateSelectorProps): ReactElement => {
	const {
		date,
		calculateHighlightRangeForDate,
		onChange,
		timezone,
		trackingId
	} = props;

	const stableValue = useValueEqualMemo(
		(): Date | null => {
			return date
				? convertToDateTime(date, timezone)
				: null;
		},
		[date, timezone]
	);

	const {
		view,
		onViewChange
	} = useCalendar(
		CalendarViewKind.Month,
		stableValue,
		timezone
	);

	useEffect(
		(): void => {
			if (isNil(stableValue)) {
				return;
			}

			onViewChange({
				viewKind: CalendarViewKind.Month,
				date: stableValue
			});
		},
		[onViewChange, stableValue]
	);

	const changeHandler = useCallback(
		(value: Date): void => {
			onChange(
				convertToDateOnly(value, timezone)
			);
		},
		[onChange, timezone]
	);

	const getCustomHighlightRange = useMemo(
		(): ((date: Date) => CalendarHighlightDateRange) | undefined => {
			if (!calculateHighlightRangeForDate) {
				return undefined;
			}

			return (date: Date): CalendarHighlightDateRange => {
				const dateOnlyRange: CalendarHighlightDateOnlyRange = calculateHighlightRangeForDate(
					convertToDateOnly(date, timezone)
				);

				return {
					start: convertToDateTime(dateOnlyRange.start, timezone),
					end: convertToDateTime(dateOnlyRange.end, timezone)
				};
			};
		},
		[calculateHighlightRangeForDate, timezone]
	);

	return (
		<Calendar
			value={stableValue}
			onChange={changeHandler}
			view={view}
			onViewChange={onViewChange}
			trackingId={trackingId}
			shouldHideToday
			timezone={timezone}
			getCustomHighlightRange={getCustomHighlightRange}
		/>
	);
};
CalendarDateSelector.displayName = "CalendarDateSelector";
